import { useEffect, useRef, useState } from "react";
import FileExplorer from "@/components/files/fileExplorer";
import { IDropDownOption } from "@/types/DropDown";
import { Grid } from "@mui/material";
import { DropDownSubjectSearch } from "@/components/forms/inputs/dropDownSubjectSearch";
import RestrictedPage from "@/consumer/restrictedPage";
import styles from "@/routes/document.module.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/helper/api";
import { useDispatch } from "react-redux";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import SelectedSubjectDisplay from "@/components/general/selectedSubjectDisplay";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { documentWorkspacePermission } from "@/helper/documentPermissions";

interface Options extends IDropDownOption {
  treeId?: string;
}

export default function Document() {
  return (
    <RestrictedPage
      permissionRequired={documentWorkspacePermission(
        window.location.hostname.split(".")[0]
      )}
    >
      <DocumentContent />
    </RestrictedPage>
  );
}

function DocumentContent() {
  const { t } = useTranslation();
  usePageHeading(t("documents"));
  /*eslint-disable */
  const [options, setOptions] = useState<Options[]>([]);
  /*eslint-enable */
  const [subjectId, setSubjectId] = useState<string>("");
  const [urlSubjectName, setUrlSubjectName] = useState<string>("");

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const { subjectId: urlSubjectId } = useParams<{ subjectId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSubjectId(selectedOption.value);
    }
  };

  const docRef = useRef(null);

  useEffect(() => {
    if (subdomain !== "admin") return;
    if (!urlSubjectId) return;
    setSubjectId(urlSubjectId);
    api.genericApiRequest({
      method: "get",
      entity: "subject",
      entityId: urlSubjectId,
      successHandler: (res: any) => {
        setUrlSubjectName(res.data?.name || "");
      },
      failHandler: () => {
        setUrlSubjectName("");
      },
    });
  }, [subdomain, urlSubjectId]);

  const handleResetToSubjectSearch = () => {
    dispatch(setSubjectSearch({}));
    setUrlSubjectName("");
    setSubjectId("");
    navigate("/document");
  };

  return (
    <Grid container>
      <Grid item xs={12} className={styles.container}>
        <Grid container className={styles.searchContainer}>
          {subdomain === "admin" && (
            <Grid item xs={12} sm={6} zIndex={10}>
              {urlSubjectId ? (
                <SelectedSubjectDisplay
                  subjectName={urlSubjectName}
                  onReset={handleResetToSubjectSearch}
                />
              ) : (
                <div>
                  <FormFieldLabel
                    label={t("subject")}
                    htmlFor="document-subject"
                  />
                  <DropDownSubjectSearch
                    name="document-subject"
                    subdomain={subdomain}
                    options={options}
                    onChange={handleSubjectSelect}
                    autofocus={true}
                    customRef={docRef}
                    useGlobalState={true}
                    isClearable={true}
                  />
                </div>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {((subdomain === "admin" && subjectId) ||
          (subdomain !== "admin" && !subjectId)) && (
          <FileExplorer
            subjectId={subjectId}
            showNewFiles={searchParams.get("view") === "new-files"}
          />
        )}
      </Grid>
    </Grid>
  );
}
